import clsx from 'clsx';
import { useId, useState } from 'react';
import { usePDPData } from '~/features/product-details/context';
import AIChatIcon from '~/icons/ai-chat.svg';
import ChevronIcon from '~/icons/chevron.svg';
import { Typography } from '~/shared/components';
import { useAiChat } from '~/shared/hooks';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductAIChat.module.scss';

type Props = React.HTMLAttributes<HTMLDivElement>;

export const ProductAIChat: React.FC<Props> = ({ className, ...rest }) => {
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    const { product } = usePDPData();

    const inputId = useId();
    const errorId = useId();

    const [message, setMessage] = useState('');
    const { isChatUnavailable, setIsChatUnavailable, openChat } = useAiChat();

    const question = message.trim();
    const canSubmitMessage = !!question;

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // The button only reports itself as unavailable, so it still submits and is stopped here.
        if (!canSubmitMessage) return;

        const productName = product.name ?? '';

        const subject =
            translate('pdp.overview.aiChatSubject', {
                productId: product.id,
                productName,
            }) || `Produkt: ${productName} (${product.id})`;

        if (openChat(`${subject}\n\n${question}`)) {
            setMessage('');
        }
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);

        // Editing the question is the retry, so the previous failure stops being news.
        setIsChatUnavailable(false);
    };

    // If AI Chatbot is disabled in Umbraco, we will not show this component.
    if (!frame.settings?.enableAIChatbot) return null;

    return (
        <div className={clsx(styles.productAIChat, className)} {...rest}>
            <div className={styles.productAIChatHeader}>
                <AIChatIcon className={styles.icon} aria-hidden />

                <Typography as="label" htmlFor={inputId} variant="paragraph-M" weight="medium">
                    {translate('pdp.overview.aiChatTitle')}
                </Typography>
            </div>

            <form className={styles.productAIChatForm} onSubmit={onSubmit}>
                <div className={styles.productAIChatField}>
                    <input
                        className={styles.productAIChatInput}
                        id={inputId}
                        type="text"
                        name="message"
                        value={message}
                        placeholder={translate('pdp.overview.aiChatInputPlaceholder')}
                        enterKeyHint="send"
                        aria-describedby={isChatUnavailable ? errorId : undefined}
                        onChange={onChange}
                    />

                    <button
                        className={styles.productAIChatSubmit}
                        type="submit"
                        aria-disabled={!canSubmitMessage}
                        aria-describedby={isChatUnavailable ? errorId : undefined}
                    >
                        <Typography variant="paragraph-XS" weight="medium">
                            {translate('pdp.overview.aiChatButtonText')}
                        </Typography>

                        <ChevronIcon className={styles.icon} aria-hidden />
                    </button>
                </div>

                <Typography
                    id={errorId}
                    className={styles.productAIChatError}
                    role="alert"
                    as="p"
                    variant="paragraph-S"
                >
                    {isChatUnavailable ? translate('pdp.overview.aiChatErrorText') : null}
                </Typography>
            </form>
        </div>
    );
};
